import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api } from '@/lib/api';
import type { User, Plan } from '@/types';

interface AuthContextType {
  user: User | null;
  plan: Plan | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      const session = await api.getMe();
      setUser(session.user);
      setPlan(session.plan);
    } catch {
      setUser(null);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const session = await api.login(email, password);
    setUser(session.user);
    setPlan(session.plan);
  };

  const register = async (email: string, password: string, displayName?: string) => {
    await api.register(email, password, displayName);
  };

  const forgotPassword = async (email: string) => {
    await api.forgotPassword(email);
  };

  const resetPassword = async (token: string, password: string) => {
    await api.resetPassword(token, password);
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
    setPlan(null);
  };

  useEffect(() => {
    if (api.isAuthenticated()) {
      refreshSession();
    } else {
      setLoading(false);
    }
  }, [refreshSession]);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  return (
    <AuthContext.Provider value={{ user, plan, loading, login, register, forgotPassword, resetPassword, logout, refreshSession, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}